import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sanitizeRedirectPath } from '@arthelper/shared/safe-redirect';

import { useDoneCta } from '../useDoneCta';
import { useDoneConfetti } from '../useDoneConfetti';
import { useDoneStats } from '../useDoneStats';
import { WELCOME_RETURN_TO_KEY } from '../index';

export default function Done() {
  const { t } = useTranslation('welcome-onboarding');
  const { uploaded, following, communities, communityUrl, profileUrl } = useDoneStats();
  const trackCta = useDoneCta();
  useDoneConfetti();

  const statItems = useMemo(
    () => [
      { value: uploaded, label: t('done.stat_label_uploaded') },
      { value: following, label: t('done.stat_label_following') },
      { value: communities, label: t('done.stat_label_communities') },
    ],
    [t, uploaded, following, communities]
  );

  // Where she was heading before the wizard detour (a referred signup's quest page,
  // forwarded by the onboarding gate). Query param first, sessionStorage fallback —
  // sanitized either way. When present it becomes the PRIMARY continue action.
  const [returnTo] = useState<string | undefined>(() => {
    const fromQuery = sanitizeRedirectPath(new URLSearchParams(window.location.search).get('redirect_url'), undefined);
    if (fromQuery && fromQuery !== '/') return fromQuery;
    try {
      return sanitizeRedirectPath(sessionStorage.getItem(WELCOME_RETURN_TO_KEY), undefined);
    } catch {
      return undefined;
    }
  });
  const returnToUrl = returnTo && returnTo !== '/' ? `${__TOWN_SQUARE_URL__}${returnTo}` : null;

  return (
    <div className="ob-done-root">
      <div className="ob-done-dot-grid" />

      <div className="ob-done-card">
        <p className="ob-done-label">{t('done.title')}</p>
        <h1 className="ob-done-title">{t('done.subtitle')}</h1>
        <p className="ob-done-subtitle">{t('done.body')}</p>

        <div className="ob-done-stats">
          {statItems.map((item, i) => (
            <div key={i} className="ob-done-stat">
              <p className="ob-done-stat-value">{item.value.toLocaleString()}</p>
              <p className="ob-done-stat-label">{item.label}</p>
            </div>
          ))}
        </div>

        <div className="ob-done-actions">
          <p className="ob-done-actions-explainer">{t('done.actions_explainer')}</p>

          <div className="ob-done-actions-row">
            {returnToUrl && (
              <a
                className="ob-done-btn-primary"
                href={returnToUrl}
                data-testid="done-continue"
                onClick={() => trackCta('continue')}
              >
                {t('done.link_continue')} →
              </a>
            )}
            <a className="ob-done-btn-muted" href={communityUrl} onClick={() => trackCta('community')}>
              {t('done.link_community')} →
            </a>
            {profileUrl && (
              <a className="ob-done-btn-muted" href={profileUrl} onClick={() => trackCta('profile')}>
                {t('done.link_artwork')} →
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
